import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import { Icon } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import Search from 'app/modules/Search';
import InputWithAnalytics from 'app/widgets/InputsWithAnalytics/Input';

import { useSearchParams } from '../useSearchParams';

import dictionary from './Input.dictionary.json';

import styles from './Input.module.scss';

const INPUT_ID = 'search-input';

const Input: FC = () => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isSearchFocused, onChangeFocusValue] = useState(false);

  const { setSearchedValue, removeSearchKey, searchValue } = useSearchParams();

  useEffect(() => {
    if (searchValue !== inputValue) {
      setInputValue(searchValue);
    }
  }, [searchValue]);

  const onFocus = useCallback((): void => {
    onChangeFocusValue(true);
  }, [onChangeFocusValue]);

  const changeInputValue = useCallback((value: string) => {
    setInputValue(value);
    setSearchedValue(value);
  }, [setSearchedValue]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    changeInputValue(value);
  }, [changeInputValue]);

  const clearInputValue = useCallback((): void => {
    changeInputValue('');
    onChangeFocusValue(false);
  }, []);

  const handleOnChangeFocusValue = useCallback(() => {
    onChangeFocusValue(false);
  }, []);

  const onLinkClick = useCallback(() => {
    handleOnChangeFocusValue();
    removeSearchKey();
  }, [removeSearchKey]);

  return (
    <>
      <div className={styles.searchForm}>
        <InputWithAnalytics
          type="text"
          id={INPUT_ID}
          value={inputValue}
          onFocus={onFocus}
          onChange={onChange}
          className={styles.searchLine}
          placeholder={t(dictionary.placeholder)}
          data-test={INPUT_ID}
          data-ga-event={INPUT_ID} />


        <button
          type="button"
          className={styles.closeButton}
          onClick={searchValue ? clearInputValue : removeSearchKey}
          data-test="cancelSearch-icon-wrapper">
          <Icon icon="close-m"
                size={14} />
        </button>
      </div>

      <CSSTransition in={Boolean(searchValue) && isSearchFocused}
                     timeout={200}
                     unmountOnExit
                     classNames="search-dropdown">
        <div className={styles.dropdownContainer}>
          <Search onLinkClick={onLinkClick}
                  className={styles.searchDropdown} />

          <div className={styles.overlayContainer}>
            <div className={styles.overlay} />
          </div>
        </div>
      </CSSTransition>
    </>
  );
};

export default Input;
